import React from "react";
import { useTranslation } from "react-i18next";

interface ModalWrapperProps {
  children: React.ReactNode;
  onClose: () => void;
  showBackButton?: boolean;
  onBack?: () => void;
  showCloseButton?: boolean;
  disableBackdropClose?: boolean;
}

const ModalWrapper: React.FC<ModalWrapperProps> = ({
  children,
  onClose,
  showBackButton = false,
  onBack,
  showCloseButton = true,
  disableBackdropClose = false,
}) => {
  const { t } = useTranslation()
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (disableBackdropClose) {
      return;
    }
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/80 flex items-center justify-center lg:p-[1%] p-4 z-50"
      onClick={handleBackdropClick}
    >
      <div className="modal bg-white-500 2xl:rounded-[2.083vw] lg:rounded-[1.5vw] rounded-3xl lg:p-[2vw] md:p-7 p-4 landscape:max-lg:p-5 2xl:max-w-[26vw] max-w-100  w-full relative animate-[slideUp_0.3s_ease-out] lg:border-[0.2vw] border-3 border-primary auth-page overflow-auto lg:max-h-[calc(100dvh-11%)] max-h-[calc(100dvh-40px)] hide-scroll">
        {/* Header with Back and Close buttons */}
        <div className="flex lg:gap-[1vw] gap-2 items-start justify-between w-full lg:mb-[1vw] mb-3">
          {showBackButton && onBack && (
            <button
              type="button"
              onClick={onBack}
              className="lg:text-[1.25vw]/tight text-lg/tight font-semibold flex lg:gap-[0.5vw] gap-2 items-center text-black-800 transition-colors cursor-pointer focus:outline-none"
            >
              <svg
                className="rtl:-scale-x-100 lg:w-[1.2vw] lg:h-[1.2vw] md:w-5 w-4 md:h-5 h-4"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M19.7291 5.74392V13.532C19.7291 14.3452 19.0662 15.0065 18.2533 15.0065H10.6629C10.286 15.0065 9.98042 15.3131 9.98042 15.6898V18.2286C9.98042 18.6727 9.73179 19.0377 9.31756 19.1986C8.90398 19.3594 8.47357 19.2593 8.17329 18.9326L0.276258 10.3409C-0.092086 9.94012 -0.092086 9.33351 0.276258 8.93278L8.17329 0.341062C8.37026 0.126797 8.63346 0 8.92559 0C9.06007 0 9.1924 0.0286874 9.31751 0.0772499C9.7325 0.238219 9.98037 0.602437 9.98037 1.04733V3.58387C9.98037 3.96056 10.286 4.26722 10.6629 4.26722H18.2532C19.0675 4.26722 19.7291 4.92984 19.7291 5.74392Z"
                  fill="#413938"
                />
              </svg>
              <span className="lg:mt-[0.2vw] mt-0.5">{t("login.back")}</span>
            </button>
          )}
          {/* Close Button */}
          {showCloseButton && (
            <button
              onClick={onClose}
              className="lg:w-[1.2vw] lg:h-[1.2vw] md:w-5 w-4 md:h-5 h-4 flex items-center justify-center cursor-pointer ms-auto hover:scale-110 transition-transform duration-300"
            >
            <svg className="w-full h-full" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M15.9474 12.1123C15.8856 12.0504 15.8856 11.9496 15.9474 11.8878L23.5029 4.3286C24.149 3.68231 24.149 2.63063 23.5029 1.98429L22.0051 0.48573C21.692 0.172468 21.2758 0 20.833 0C20.3902 0 19.9739 0.172468 19.6609 0.485683L12.1054 8.04493C12.0672 8.08316 12.0235 8.09122 11.9937 8.09122C11.9639 8.09122 11.9203 8.08321 11.8821 8.04493L4.32658 0.485683C4.01351 0.172468 3.59726 0 3.15445 0C2.71164 0 2.29544 0.172468 1.98236 0.485683L0.484547 1.98429C-0.161516 2.63063 -0.161516 3.68231 0.484547 4.3286L8.04001 11.8878C8.10185 11.9496 8.10185 12.0504 8.04001 12.1123L0.484547 19.6714C-0.161469 20.3178 -0.161469 21.3694 0.484547 22.0157L1.98236 23.5143C2.29544 23.8275 2.71168 24 3.15445 24C3.59721 24 4.01351 23.8275 4.32658 23.5143L11.882 15.9551C11.9203 15.9169 11.9638 15.9089 11.9937 15.9089C12.0235 15.9089 12.0671 15.9169 12.1053 15.9552L19.6608 23.5143C19.9739 23.8276 20.3901 24 20.8329 24C21.2758 24 21.692 23.8276 22.005 23.5143L23.5028 22.0158C24.1489 21.3695 24.1489 20.3178 23.5028 19.6715L15.9474 12.1123Z"
                fill="#413938"
              />
            </svg>
          </button>
          )}
        </div>
        {children}
      </div>
      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default ModalWrapper;

